/* eslint-disable react/no-unescaped-entities */
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Loader2, Paperclip, Send, FileText, ImageIcon, Check, CheckCheck } from "lucide-react";

const POLL_INTERVAL_MS = 4000;

/** CollabDiscussion — premium chat tab tied to a single collaboration.
 * Polls /messages every 4s while the tab is visible.
 * Renders bubbles with avatars, timestamps, read indicators, attachments (image + PDF).
 */
export default function CollabDiscussion({ collaborationId }) {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [participants, setParticipants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [body, setBody] = useState("");
  const [pendingAttachments, setPendingAttachments] = useState([]); // [{kind,url,filename,size,...}]
  const [uploading, setUploading] = useState(false);
  const lastFetchedAtRef = useRef(null);
  const scrollRef = useRef(null);
  const fileInputRef = useRef(null);

  const participantsById = participants.reduce((acc, p) => ({ ...acc, [p.user_id]: p }), {});

  // ---------- Polling ----------
  const fetchMessages = useCallback(async () => {
    try {
      const since = lastFetchedAtRef.current;
      const url = `/collaborations/${collaborationId}/messages${since ? `?since=${encodeURIComponent(since)}` : ""}`;
      const { data } = await api.get(url);
      const incoming = data.messages || [];
      if (incoming.length) {
        setMessages((prev) => {
          const seen = new Set(prev.map((m) => m.id));
          const merged = [...prev];
          for (const m of incoming) if (!seen.has(m.id)) merged.push(m);
          return merged;
        });
        lastFetchedAtRef.current = incoming[incoming.length - 1].created_at;
      }
    } catch (e) {
      // fail silently — polling will retry
    }
  }, [collaborationId]);

  // ---------- Initial load ----------
  useEffect(() => {
    setLoading(true);
    Promise.all([
      api.get(`/collaborations/${collaborationId}/participants`).then(({ data }) => setParticipants(data.participants || [])),
      api.get(`/collaborations/${collaborationId}/messages`).then(({ data }) => {
        const msgs = data.messages || [];
        setMessages(msgs);
        if (msgs.length) lastFetchedAtRef.current = msgs[msgs.length - 1].created_at;
      }),
    ])
      .catch(() => toast.error("Impossible de charger la discussion"))
      .finally(() => setLoading(false));
  }, [collaborationId]);

  // ---------- Poll loop ----------
  useEffect(() => {
    const id = setInterval(() => {
      if (document.visibilityState === "visible") fetchMessages();
    }, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [fetchMessages]);

  // ---------- Mark read whenever the message list changes (and we have unseen items) ----------
  useEffect(() => {
    if (!messages.length) return;
    const lastFromOther = [...messages].reverse().find((m) => m.sender_user_id !== user?.user_id);
    if (!lastFromOther) return;
    api.post(`/collaborations/${collaborationId}/messages/read`).catch(() => {});
  }, [messages, user?.user_id, collaborationId]);

  // ---------- Auto-scroll to bottom on new messages ----------
  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
  }, [messages.length]);

  // ---------- Send ----------
  const handleSend = async (e) => {
    e?.preventDefault?.();
    const trimmed = body.trim();
    if (!trimmed && pendingAttachments.length === 0) return;
    setSending(true);
    try {
      const { data: msg } = await api.post(`/collaborations/${collaborationId}/messages`, {
        body: trimmed,
        attachments: pendingAttachments,
      });
      setMessages((prev) => (prev.some((m) => m.id === msg.id) ? prev : [...prev, msg]));
      lastFetchedAtRef.current = msg.created_at;
      setBody("");
      setPendingAttachments([]);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Envoi impossible");
    } finally {
      setSending(false);
    }
  };

  // ---------- Upload attachment ----------
  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/collaborations/${collaborationId}/messages/upload`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPendingAttachments((prev) => [...prev, data]);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Échec du téléversement");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20" data-testid="collab-chat-loading">
        <Loader2 className="w-7 h-7 animate-spin text-[#C68E59]" />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-[min(70vh,640px)] sm:h-[640px] rounded-3xl bg-white border border-[#EADDC9] overflow-hidden shadow-[0_4px_24px_rgba(110,75,42,0.05)]" data-testid="collab-discussion">
      {/* Header — participants */}
      <div className="flex items-center gap-3 px-4 sm:px-5 py-3 border-b border-[#EADDC9] bg-[#FAF3EA]/60">
        <div className="flex -space-x-2">
          {participants.slice(0, 4).map((p) => (
            <Avatar key={p.user_id} user={p} size={32} />
          ))}
        </div>
        <div className="min-w-0">
          <p className="text-[13px] font-medium text-[#1A1A1A] truncate">
            {participants.map((p) => `${p.first_name || ""} ${p.last_name || ""}`.trim() || "Membre").join(" · ")}
          </p>
          <p className="text-[11px] text-[#A26F3E]">
            {participants.length} participant{participants.length > 1 ? "s" : ""}
          </p>
        </div>
      </div>

      {/* Messages list */}
      <div
        ref={scrollRef}
        className="flex-1 overflow-y-auto px-4 sm:px-5 py-5 space-y-4 bg-gradient-to-b from-white to-[#FAF3EA]/30"
        data-testid="collab-discussion-messages"
        style={{ scrollBehavior: "smooth", overscrollBehavior: "contain" }}
      >
        {messages.length === 0 && (
          <div className="text-center py-10 text-[#A26F3E] text-sm">
            Aucun message pour le moment. Démarre la conversation ✨
          </div>
        )}
        <AnimatePresence initial={false}>
          {messages.map((m, i) => (
            <MessageBubble
              key={m.id}
              message={m}
              isMine={m.sender_user_id === user?.user_id}
              sender={participantsById[m.sender_user_id]}
              showAvatar={i === 0 || messages[i - 1]?.sender_user_id !== m.sender_user_id}
            />
          ))}
        </AnimatePresence>
      </div>

      {/* Pending attachments */}
      {pendingAttachments.length > 0 && (
        <div className="flex flex-wrap gap-2 px-4 sm:px-5 py-2 border-t border-[#EADDC9] bg-[#FAF3EA]/50">
          {pendingAttachments.map((a, idx) => (
            <div key={a.file_id} className="flex items-center gap-2 rounded-full bg-white border border-[#EADDC9] px-3 py-1.5 text-xs">
              {a.kind === "image" ? <ImageIcon className="w-3 h-3 text-[#A26F3E]" /> : <FileText className="w-3 h-3 text-[#A26F3E]" />}
              <span className="max-w-[160px] truncate">{a.filename}</span>
              <button
                onClick={() => setPendingAttachments((prev) => prev.filter((_, i) => i !== idx))}
                className="text-[#A26F3E] hover:text-[#1A1A1A]"
                data-testid={`chat-attachment-remove-${idx}`}
              >
                ×
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Composer */}
      <form onSubmit={handleSend} className="flex items-end gap-2 px-3 sm:px-4 py-3 border-t border-[#EADDC9] bg-white" data-testid="collab-discussion-composer">
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp,image/heic,image/heif,application/pdf"
          className="hidden"
          onChange={handleFileChange}
        />
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading || sending}
          className="flex-shrink-0 w-10 h-10 rounded-full bg-[#FAF3EA] hover:bg-[#EADDC9] text-[#A26F3E] flex items-center justify-center transition-colors disabled:opacity-50"
          data-testid="collab-discussion-attach"
        >
          {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Paperclip className="w-4 h-4" />}
        </button>
        <textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              handleSend(e);
            }
          }}
          placeholder="Écris un message…"
          rows={1}
          className="flex-1 resize-none rounded-2xl border border-[#EADDC9] bg-[#FAF3EA]/50 focus:bg-white focus:border-[#C68E59] focus:outline-none px-4 py-2.5 text-sm leading-relaxed max-h-32 transition-colors"
          data-testid="collab-discussion-input"
        />
        <Button
          type="submit"
          disabled={sending || (!body.trim() && pendingAttachments.length === 0)}
          className="flex-shrink-0 w-10 h-10 rounded-full bg-[#C68E59] hover:bg-[#A26F3E] text-white p-0 disabled:opacity-40"
          data-testid="collab-discussion-send"
        >
          {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
        </Button>
      </form>
    </div>
  );
}

/* ============================== Sub-components ============================== */
function Avatar({ user, size = 36 }) {
  const initials = `${user?.first_name?.[0] || ""}${user?.last_name?.[0] || ""}`.toUpperCase() || "?";
  const style = { width: size, height: size };
  if (user?.avatar_url) {
    return <img src={user.avatar_url} alt="" className="rounded-full object-cover ring-2 ring-white" style={style} />;
  }
  return (
    <div
      className="rounded-full bg-gradient-to-br from-[#E6C29A] to-[#A26F3E] text-white text-[11px] font-medium flex items-center justify-center ring-2 ring-white"
      style={style}
    >
      {initials}
    </div>
  );
}

function MessageBubble({ message, isMine, sender, showAvatar }) {
  const time = new Date(message.created_at).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25, ease: "easeOut" }}
      className={`flex gap-2 ${isMine ? "flex-row-reverse" : "flex-row"}`}
      data-testid={`chat-bubble-${message.id}`}
    >
      <div className="w-9 flex-shrink-0 self-end">
        {showAvatar && <Avatar user={sender} size={32} />}
      </div>
      <div className={`flex flex-col max-w-[78%] sm:max-w-[68%] ${isMine ? "items-end" : "items-start"}`}>
        {showAvatar && !isMine && sender && (
          <span className="text-[11px] text-[#A26F3E] mb-0.5 px-2">
            {sender.first_name} {sender.last_name}
          </span>
        )}
        {(message.body || (message.attachments || []).length === 0) && (
          <div
            className={`px-4 py-2.5 text-[14.5px] leading-relaxed whitespace-pre-wrap break-words ${
              isMine
                ? "bg-gradient-to-br from-[#C68E59] to-[#A26F3E] text-white rounded-[20px] rounded-br-[6px]"
                : "bg-[#FAF3EA] text-[#1A1A1A] rounded-[20px] rounded-bl-[6px]"
            }`}
          >
            {message.body || "—"}
          </div>
        )}
        {(message.attachments || []).map((a) => (
          <Attachment key={a.file_id} attachment={a} isMine={isMine} />
        ))}
        <div className={`flex items-center gap-1 mt-1 px-2 text-[10px] ${isMine ? "text-[#A26F3E]" : "text-[#8B8680]"}`}>
          <span>{time}</span>
          {isMine && <CheckCheck className="w-3 h-3 opacity-70" />}
        </div>
      </div>
    </motion.div>
  );
}

function Attachment({ attachment, isMine }) {
  if (attachment.kind === "image") {
    return (
      <a href={attachment.url} target="_blank" rel="noreferrer" className="mt-1.5 block rounded-2xl overflow-hidden max-w-[260px] hover:opacity-95 transition-opacity">
        <img src={attachment.url} alt={attachment.filename} className="w-full h-auto object-cover" loading="lazy" />
      </a>
    );
  }
  return (
    <a
      href={attachment.url}
      target="_blank"
      rel="noreferrer"
      className={`mt-1.5 inline-flex items-center gap-2 px-3 py-2 rounded-2xl text-xs max-w-[260px] hover:opacity-90 transition-opacity ${
        isMine ? "bg-white/15 text-white border border-white/25" : "bg-white border border-[#EADDC9] text-[#1A1A1A]"
      }`}
    >
      <FileText className="w-4 h-4 flex-shrink-0" />
      <span className="truncate flex-1">{attachment.filename}</span>
      <span className="opacity-60 text-[10px]">{Math.ceil((attachment.size || 0) / 1024)}KB</span>
    </a>
  );
}

// Defensive: silence unused vars lint if any
void Check;
